import { useState } from "react";
import { useSelector } from "react-redux";
import { Form, Row, Col, Button } from "react-bootstrap";
import DialogFooter from "./DialogFooter.js";
import { buildDashboardModule, buildDashboardPayload } from "./dashboardPayloadConstants.js";

const CreateTaskView = ({
  initialSettings,
  onHide,
  isUpdateMode = false,
  taskCount,
  reloadDashboard,
  toastRef,
}) => {
  // Redux user info
  const { userInformation } = useSelector((state) => state.UserReducer);
  // Task Family options
  const [submitting, setSubmitting] = useState(false);
  const defaultTaskFamilyOptions = [
    { label: "Approval", value: "Approval", checked: false },
    { label: "Define", value: "Define", checked: false },
    { label: "Confirm", value: "Confirm", checked: false },
    { label: "Upload", value: "Upload", checked: false },
    { label: "Request", value: "Request", checked: false },
  ];
  const initialTaskFamily = initialSettings?.TaskFamily?.split(",").map((f) => f.trim()) || [];
  const [taskFamilyOptions, setTaskFamilyOptions] = useState(
    defaultTaskFamilyOptions.map((opt) => ({
      ...opt,
      checked: initialTaskFamily.includes(opt.label),
    })),
  );

  // Task Type options
  const defaultTaskTypeOptions = [
    { label: "CPPFA", value: "CPPFA", checked: false },
    { label: "DI", value: "DI", checked: false },
    { label: "RDT", value: "RDT", checked: false },
    { label: "CD/PT", value: "CD/PT", checked: false },
    { label: "Ink Qual.", value: "Ink Qual", checked: false },
    { label: "CIC/CICM", value: "CIC/CICM", checked: false },
    { label: "Upload Briefing doc", value: "Upload Briefing doc", checked: false },
    { label: "Dependency Mapping", value: "Dependency Mapping", checked: false },
    { label: "AW Scope Alignment", value: "AW Scope Alignment", checked: false },
    { label: "T-POA", value: "T-POA", checked: false },
    { label: "Custom Fullkit", value: "Custom Fullkit", checked: false },
  ];
  const initialTaskType = initialSettings?.TaskType?.split(",").map((f) => f.trim()) || [];
  const [taskTypeOptions, setTaskTypeOptions] = useState(
    initialSettings?.taskTypeOptions
      ? initialSettings.taskTypeOptions
      : defaultTaskTypeOptions.map((opt) => ({
          ...opt,
          checked: initialTaskType.includes(opt.value),
        })),
  );

  // Task Status options
  const defaultTaskStatusOptions = [
    { label: "In-Progress", value: "In-Progress", checked: false },
    { label: "Complete", value: "Complete", checked: false },
    { label: "Complete-Approved", value: "Complete-Approved", checked: false },
    { label: "Complete-Rejected", value: "Complete-Rejected", checked: false },
    { label: "Awaiting", value: "Awaiting", checked: false },
  ];
  const initialTaskStatus = initialSettings?.TaskStatus?.split(",").map((f) => f.trim()) || [];
  const [taskStatusOptions, setTaskStatusOptions] = useState(
    initialSettings?.taskStatusOptions
      ? initialSettings.taskStatusOptions
      : defaultTaskStatusOptions.map((opt) => ({
          ...opt,
          checked: initialTaskStatus.includes(opt.value),
        })),
  );

  // From options
  const defaultFromOptions = [
    { label: "My Tasks", value: "My Tasks", checked: false },
    { label: "My Projects", value: "My Projects", checked: false },
    { label: "All Projects", value: "All Projects", checked: false },
  ];
  const initialFrom = initialSettings?.From?.split(",").map((f) => f.trim()) || [];
  const [fromOptions, setFromOptions] = useState(
    initialSettings?.fromOptions
      ? initialSettings.fromOptions
      : defaultFromOptions.map((opt) => ({
          ...opt,
          checked: initialFrom.includes(opt.value),
        })),
  );

  const handleCheckboxChange = (optionType, index) => {
    const setters = {
      taskFamily: setTaskFamilyOptions,
      taskType: setTaskTypeOptions,
      taskStatus: setTaskStatusOptions,
      from: setFromOptions,
    };

    const getters = {
      taskFamily: taskFamilyOptions,
      taskType: taskTypeOptions,
      taskStatus: taskStatusOptions,
      from: fromOptions,
    };

    const updatedOptions = [...getters[optionType]];
    updatedOptions[index].checked = !updatedOptions[index].checked;
    setters[optionType](updatedOptions);
  };

  const buildDashboardTaskPayload = () => {
    // Extract checked values for each option type
    const getChecked = (options) =>
      options
        .filter((opt) => opt.checked)
        .map((opt) => opt.value.trim())
        .join(",");

    const userId = initialSettings?.User_ID || userInformation?.userid;
    // Handle taskCount undefined or NaN
    let moduleName;
    if (isUpdateMode) {
      moduleName = initialSettings?.Title;
    } else {
      const count = typeof taskCount === "number" && !isNaN(taskCount) ? taskCount : 0;
      moduleName = `Tasks_${count + 1}`;
    }
    const module = buildDashboardModule({
      moduleId: isUpdateMode ? initialSettings?.Module_ID || "" : "",
      moduleName: moduleName,
      moduleType: "Task",
      userId,
      config: {
        TaskFamily: getChecked(taskFamilyOptions),
        TaskStatus: getChecked(taskStatusOptions),
        TaskType: getChecked(taskTypeOptions),
        From: getChecked(fromOptions),
      },
      Sequence: isUpdateMode ? initialSettings?.Sequence : -1
    });
    return buildDashboardPayload({ module });
  };

  const handleCreateOrUpdate = async () => {
    setSubmitting(true);
    const payload = buildDashboardTaskPayload();
    try {
      // Import createOrUpdateDashboardModuleView dynamically to avoid circular deps
      const { createOrUpdateDashboardModuleView } =
        await import("../../../apis/dashboardApi.js");
      await createOrUpdateDashboardModuleView(payload);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: isUpdateMode
            ? "Task module updated successfully"
            : "Task module created successfully",
          life: 3000,
        });
      }
      if (typeof reloadDashboard === "function") reloadDashboard();
      onHide();
    } catch (err) {
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to create/update task module.",
          life: 5000,
        });
      }
      console.error("Error creating/updating task module:", err);
      setSubmitting(false);
    }
  };

  const isFormValid =
    taskFamilyOptions.some((option) => option.checked) &&
    taskTypeOptions.some((option) => option.checked) &&
    taskStatusOptions.some((option) => option.checked) &&
    fromOptions.some((option) => option.checked);

  return (
    <>
      <Row className="task-form">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="module-form-label">
              Task Family <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="mt-2">
              {taskFamilyOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`taskFamily-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleCheckboxChange("taskFamily", index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label className="module-form-label">
              Task Type <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="mt-2">
              {taskTypeOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`taskType-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleCheckboxChange("taskType", index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label className="module-form-label">
              Task Status <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="mt-2">
              {taskStatusOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`taskStatus-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleCheckboxChange("taskStatus", index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label className="module-form-label">
              From <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="mt-2">
              {fromOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`from-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleCheckboxChange("from", index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <DialogFooter
        submitting={submitting}
        isFormValid={isFormValid}
        isUpdateMode={isUpdateMode}
        onCancel={onHide}
        onSubmit={handleCreateOrUpdate}
      />
    </>
  );
};

export default CreateTaskView;
